import { Link } from 'react-router-dom';
import {
  UXP_EI_ID,
  COMMAND_CENTER_METRICS,
  BRIEFING_TYPES,
  ANALYTICS_SOURCES,
  FORECAST_METRICS,
  SCENARIO_TYPES,
  DECISION_FEATURES,
  INSIGHT_TYPES,
  NL_ANALYTICS_FEATURES,
  REPORT_BUILDER_FEATURES,
  DASHBOARD_BUILDER_FEATURES,
  BENCHMARKING_FEATURES,
  COPILOT_CAPABILITIES,
  COLLABORATION_FEATURES,
  ANALYTICS_METRICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_BRIEFINGS,
  DEMO_SOURCES,
  DEMO_FORECASTS,
  DEMO_SCENARIOS,
  DEMO_DECISIONS,
  DEMO_INSIGHTS,
  DEMO_COPILOT,
  buildExecutiveIntelDashboard,
} from '../../executiveintel/framework';
import {
  ExecutiveCommandCenterPanel,
  AIExecutiveBriefingsPanel,
  UnifiedAnalyticsPanel,
  ForecastEnginePanel,
  ScenarioSimulationPanel,
  DecisionEnginePanel,
  AIInsightEnginePanel,
  AIExecutiveCopilotPanel,
} from '../../components/executiveintel';
import { Card, KpiCard } from '../../components/ui';

export function ExecutiveIntelligencePage() {
  const dashboard = buildExecutiveIntelDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_EI_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Executive Intelligence Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Executive Intelligence, Business Analytics, Forecasting & Decision Intelligence Operating System —
          unified AI-driven decision support across every business function.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-100 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Briefing Types" value={dashboard.briefing_types.toString()} />
        <KpiCard label="Analytics Sources" value={dashboard.analytics_sources.toString()} />
        <KpiCard label="Forecast Metrics" value={dashboard.forecast_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ExecutiveCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Executive Briefings</h2>
        <p className="mt-1 text-sm text-slate-500">{BRIEFING_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIExecutiveBriefingsPanel briefings={DEMO_BRIEFINGS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Unified Analytics</h2>
        <p className="mt-1 text-sm text-slate-500">{ANALYTICS_SOURCES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <UnifiedAnalyticsPanel sources={DEMO_SOURCES} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Forecast Engine</h2>
          <p className="mt-1 text-sm text-slate-500">{FORECAST_METRICS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ForecastEnginePanel forecasts={DEMO_FORECASTS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Scenario Simulation</h2>
          <p className="mt-1 text-sm text-slate-500">{SCENARIO_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <ScenarioSimulationPanel scenarios={DEMO_SCENARIOS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Decision Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{DECISION_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DecisionEnginePanel decisions={DEMO_DECISIONS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Insight Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{INSIGHT_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIInsightEnginePanel insights={DEMO_INSIGHTS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Executive Copilot</h2>
        <p className="mt-1 text-sm text-slate-500">{COPILOT_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIExecutiveCopilotPanel recommendations={DEMO_COPILOT} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Natural Language Analytics</h2>
          <p className="mt-1 text-sm text-slate-500">{NL_ANALYTICS_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p className="font-medium">&quot;Why did profit drop 2% last week?&quot;</p>
              <p className="text-slate-600 dark:text-slate-400">Storage fees +₹42K · Returns +18% · Partially offset by revenue +11%</p>
            </div>
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Benchmarking</h2>
          <p className="mt-1 text-sm text-slate-500">{BENCHMARKING_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <div className="space-y-2 text-sm">
              <p>Revenue vs target: <span className="font-semibold text-success-600">+8% ahead</span></p>
              <p>Amazon.in vs .com: <span className="font-semibold">62/38 split · .com +22% growth</span></p>
            </div>
          </Card>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Report Builder</h2>
          <p className="mt-1 text-sm text-slate-500">{REPORT_BUILDER_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Dashboard Builder</h2>
          <p className="mt-1 text-sm text-slate-500">{DASHBOARD_BUILDER_FEATURES.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <p className="mt-2 text-xs text-slate-500">{COLLABORATION_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{ANALYTICS_METRICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
